import { useMemo, useState } from "react";
import dayjs from "dayjs";
import _ from "lodash";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import Select from "react-select";

//Style
import "./style.scss";

// Components
import Tooltip from "components/atomic/shared/Tooltip";
import ModalKonfirmasi from "components/molecules/shared/modal-konfirmasi";
// Button Icon
import ButtonV1 from "components/v1/Button";
import Modal from "components/modal";
import TableListListing from "pages/dashboard/list-listing/ListListing/table-list-listing.js";
import Filter from "components/molecules/list-listing/filter.js";

//Assets
import { ReactComponent as PlusIcon } from "assets/plus.svg";
import { ReactComponent as InfoIcon } from "assets/info-blue.svg";
import { ReactComponent as CrossIcon } from "assets/cross.svg";
import IMagnifier from "assets/i-magnifier.js";
import IDownload from "assets/i-download.js";
import IRefresh from "assets/i-refresh.js";
import IDelete from "assets/i-trash.js";
import ICheckRounded from "assets/i-check-rounded.js";
import IFilter from "assets/i-filter.js";

//Context
import { useDetailListingCtx } from "../Context";

//Utils
import { useGlobalCtx } from "contexts";

const ButtonFilter = ({ onSelect, selectedFilter, value, label }) => {
  const isActive = selectedFilter === value;
  return (
    <button
      className={cn(
        "text-[11px] font-inter-semibold leading-[17.798px] tracking-[-0.4px] w-[75px] h-[24px] flex items-center justify-center",
        {
          "text-[#25282B] bg-white rounded-[4px]": isActive,
          "text-[#808285]": !isActive,
        }
      )}
      onClick={() => onSelect(value)}
    >
      {label}
    </button>
  );
};

function ListListing() {
  const navigate = useNavigate();
  const { profile } = useGlobalCtx();

  // State for period selection modal
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    listDetail,
    search,
    searchHandler,
    agents,
    resetFilter,
    exportToExcel,
    company,
    handleGetDetailListing,
    params,
    setParams,
    totalPage,
    isLoading,
    listData,
    setListDetail,
    selectedListings,
    setSelectedListings,
    handleActiveBulkProperties,
    handleDeleteBulkProperties,
  } = useDetailListingCtx();

  const handleFilter = (newFilter) => {
    setListDetail(null);
    setParams({ ...params, page: 1, filter: newFilter });
  };

  const onSelectHandler = async (selectedAgent, options) => {
    const agent = options.find((option) => option?.id === selectedAgent?.value);

    setParams({ ...params, agent, page: 1 });
  };

  const handleDownloadClick = () => {
    setShowPeriodModal(true);
  };

  // Check if the date range exceeds 3 months
  const isValidDateRange = () => {
    if (!periodFrom || !periodTo) return false;

    const startDate = dayjs(periodFrom);
    const endDate = dayjs(periodTo);

    // Check if end date is after start date
    if (endDate.isBefore(startDate)) return false;

    // Calculate the difference in months using dayjs
    const diffMonths = endDate.diff(startDate, "month");
    const sameDay = startDate.date() === endDate.date();
    const oneMoreDay = endDate.date() < startDate.date();

    // Return true if the difference is less than or equal to 3 months
    return diffMonths < 3 || (diffMonths === 3 && (sameDay || oneMoreDay));
  };

  const handleDownload = () => {
    // Close the modal
    setShowPeriodModal(false);

    // Call the export function with the selected period
    exportToExcel(periodFrom, periodTo);

    // Reset the date fields
    setPeriodFrom("");
    setPeriodTo("");
  };

  const handleCloseModal = () => {
    setIsOpenModal("");
  };

  const handleOpenModal = (type) => {
    setIsOpenModal(type);
  };

  const showBtnListing = useMemo(() => {
    const roleId = profile?.role?.id;

    return roleId !== 36 && roleId !== 38;
  }, [profile]);

  const AgentOptions = useMemo(() => {
    const res =
      agents?.data?.map((agent) => ({
        ...agent,
        value: agent.id,
        label: agent.full_name,
      })) || [];

    if (!res?.length) return [];

    return [
      {
        value: "",
        label: "Marketing",
      },
      ...res,
    ];
  }, [agents]);

  const selectedAgent = useMemo(() => {
    if (!params?.agent?.id) {
      return AgentOptions.find((option) => option.value === "") || null;
    }
    return (
      AgentOptions.find((option) => option.value === params.agent.id) || null
    );
  }, [params.agent, AgentOptions]);

  return (
    <div className="pl-6 list-listing bg-[#ffffff] overflow-x-auto">
      <div className="flex items-center justify-between gap-4 py-6">
        <p className="text-[22px] font-inter text-[#808285] leading-[35.596px] tracking-[-0.4px]">
          Daftar Listing:{" "}
          <span className="font-inter-semibold font-[#252526]">{company}</span>
        </p>

        <div className="flex items-center gap-2 w-[310px] bg-[#F6F6F6] p-2 h-[32px] rounded-[4px] border-[0.5px] border-solid border-[#D8D8D9]">
          <div>
            <IMagnifier width="16px" height="16px" color="#252526" />
          </div>
          <input
            className="w-[calc(100%-16px)]"
            disabled={isLoading}
            value={search}
            placeholder="#idlisting/judul/alamat/deskripsi"
            onChange={searchHandler}
          />
        </div>
      </div>

      <div className="rounded-[6px] border border-solid border-[#ECECEC] bg-white">
        <div className="flex items-center justify-between px-4 py-2 gap-2">
          <ButtonV1
            text="Buat Listing"
            icon={PlusIcon}
            onClick={() => navigate("/upload-listing")}
            bg="black"
            size="l"
          />
          {showBtnListing && (
            <ButtonV1
              text="Download Listing"
              onClick={handleDownloadClick}
              icon={IDownload}
              type="secondary"
              size="l"
            />
          )}
        </div>
        <div className="py-2 px-4 flex items-center justify-between border-y-[0.5px] border-solid border-[#808285] gap-6 overflow-x-auto">
          <div className="flex items-center rounded-[5px] bg-[#E6E7E8] p-0.5 gap-2 w-fit flex-shrink-0">
            {/* <ButtonFilter
              onSelect={handleFilter}
              selectedFilter={params.filter}
              value=""
              label="Semua"
            /> */}
            <ButtonFilter
              onSelect={handleFilter}
              selectedFilter={params.filter}
              value="active"
              label="Aktif"
            />
            <ButtonFilter
              onSelect={handleFilter}
              selectedFilter={params.filter}
              value="inactive"
              label="Tidak Aktif"
            />
            <ButtonFilter
              onSelect={handleFilter}
              selectedFilter={params.filter}
              value="terjual"
              label="Terjual"
            />
            <ButtonFilter
              onSelect={handleFilter}
              selectedFilter={params.filter}
              value="tersewa"
              label="Tersewa"
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <ButtonV1
              icon={IFilter}
              type="secondary"
              onClick={() => setIsFilterOpen(true)}
              className="!bg-[#FAFAFA] !border-[#DEDFDE]"
              size="s"
              text="Filter"
              iconPosition="right"
            />

            {selectedListings?.length > 0 && (
              <>
                <ButtonV1
                  icon={ICheckRounded}
                  size="s"
                  onClick={() => handleOpenModal("konfirmasi-aktifkan")}
                  text="Aktifkan Listing"
                />
                <Tooltip content="Hapus Listing">
                  <ButtonV1
                    icon={IDelete}
                    type="negative"
                    size="s"
                    iconClassName="w-[14px] h-[14px]"
                    iconColor="#FD4949"
                    className="h-fit !py-[8px] !px-[8px]"
                    onClick={() => handleOpenModal("konfirmasi-hapus")}
                  />
                </Tooltip>
              </>
            )}
          </div>
        </div>

        <div className="table-pagination-wrapper">
          {isLoading ? (
            <div
              className="w-[100%] flex justify-center items-center"
              style={{ height: "100vh" }}
            >
              <Oval
                height={60}
                width={60}
                color="#4fa94d"
                wrapperStyle={{}}
                wrapperClass=""
                visible={true}
                ariaLabel="oval-loading"
                secondaryColor="#4fa94d"
                strokeWidth={4}
                strokeWidthSecondary={4}
              />
            </div>
          ) : (
            <TableListListing
              data={listData}
              totalData={totalPage}
              page={params.page}
              filter={params.filter}
              listDetail={listDetail}
              selectedListings={selectedListings}
              setSelectedListings={setSelectedListings}
              onSelectListing={(slug) => handleGetDetailListing(slug)}
              handleNextPage={() => {
                setParams({ ...params, page: params.page + 1 });
              }}
              handlePrevPage={() => {
                setParams({ ...params, page: params.page - 1 });
              }}
            />
          )}
        </div>
      </div>

      {/* Modal for selecting listing period */}
      {showPeriodModal && (
        <Modal
          isOpen={showPeriodModal}
          onClose={() => setShowPeriodModal(false)}
        >
          <div className="z-[42] bg-white w-[426px] rounded-[6px] border-[0.5px] border-solid border-[#ECECEC] shadow-[0_1px_3px_0_rgba(16,16,16,0.10),_0_1px_2px_0_rgba(16,16,16,0.06)]">
            <div className="px-6 py-2 rounded-t-[6px] flex items-center justify-between border-b border-solid border-[#D8D8D9]">
              <p className="text-[17px]/[27.506px] tracking-[-0.4px] font-bold">
                Pilih periode listing
              </p>
              <CrossIcon onClick={() => setShowPeriodModal(false)} />
            </div>
            <div className="px-6 py-2">
              <div className="w-full py-2 gap-6 flex items-center justify-between">
                <label className="w-[115px] text-[13px]/[21.034px] tracking-[-0.4px]">
                  Dari<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full rounded-[4px] border-[1px] border-solid border-[#D8D8D9] p-2"
                  value={periodFrom}
                  onChange={(e) => setPeriodFrom(e.target.value)}
                />
              </div>

              <div className="w-full py-2 gap-6 flex items-center justify-between">
                <label className="w-[115px] text-[13px]/[21.034px] tracking-[-0.4px]">
                  Sampai<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full rounded-[4px] border-[1px] border-solid border-[#D8D8D9] p-2"
                  value={periodTo}
                  onChange={(e) => setPeriodTo(e.target.value)}
                />
              </div>
            </div>

            <div className="px-6 py-2 bg-[#EBF0F7] flex items-center gap-2">
              <InfoIcon className="w-5 h-5" />
              <span className="text-[11px]/[17.798px] tracking-[-0.4px] text-[#3268AE]">
                Periode maksimum 3 bulan
              </span>
            </div>

            <div className="px-6 py-4 flex justify-end border-t border-solid border-[#D8D8D9]">
              <ButtonV1
                text="Download"
                onClick={handleDownload}
                disabled={!periodFrom || !periodTo || !isValidDateRange()}
                size="xl"
              />
            </div>
          </div>
        </Modal>
      )}

      <ModalKonfirmasi
        isOpen={
          isOpenModal === "konfirmasi-hapus" ||
          isOpenModal === "konfirmasi-aktifkan"
        }
        onClose={handleCloseModal}
        title={
          isOpenModal === "konfirmasi-hapus"
            ? "Hapus Listing Terpilih?"
            : "Aktifkan Listing Terpilih?"
        }
        description={
          isOpenModal === "konfirmasi-hapus"
            ? "Listing akan dihapus secara permanen dari pusat data. Jika listing ini dibutuhkan kembali, Anda perlu mendaftarkannya ulang."
            : "Listing yang terpilih akan ditayangkan kembali di ONE21, baik di publik maupun kantor."
        }
        leftBtnText="Batal"
        rightBtnText={
          isOpenModal === "konfirmasi-hapus"
            ? "Hapus Permanen"
            : "Aktifkan Kembali"
        }
        rightBtnAction={() => {
          if (isOpenModal === "konfirmasi-hapus") {
            handleDeleteBulkProperties();
          } else {
            handleActiveBulkProperties();
          }
          handleCloseModal();
        }}
        leftBtnAction={handleCloseModal}
        rightBtnVariant={
          isOpenModal === "konfirmasi-hapus" ? "negative" : "primary"
        }
        withCounter={isOpenModal === "konfirmasi-hapus"}
      />

      <Filter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} />
    </div>
  );
}

export default ListListing;
